import React, { FC, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RaceStatusEnum } from "@tvg/ts-types/Race";
import Icon from "@fdr/static-ui/Icons";
import { arrowRight } from "@fdr/static-ui/Icons/icons";
import { Paragraph, useTheme, Button } from "@tvg/design-system";
import { EVENTS } from "@urp/amplitude/src/modules/talentPicks/constants";
import TalentPicksCard from "../TalentPicksCard";
import CardsLoading from "../CardsLoading";
import {
  TalentPicksContainer,
  ContentContainer,
  LinkContainer,
  IconContainer,
  AllTalentPicksWrapper,
  CardsPgpContainer,
  DesktopTitleContainer
} from "./styled-components";
import TalentPicksPgP from "./TalentPicksPgP";
import useTalentPicksWithFilters from "../../hooks/useTalentPicksWithFilters";
import { triggerAmplitudeEvent } from "../../utils/amplitude";

interface TalentPickListProps {
  qaLabel?: string;
  size?: number;
  onHomePage?: boolean;
  moduleRef?: React.MutableRefObject<HTMLSpanElement>;
  title: string;
}

const handleSeeAll = () => {
  triggerAmplitudeEvent(EVENTS.CTA_CLICKED);
};

const TalentPickList: FC<TalentPickListProps> = ({
  qaLabel = "talentPicks",
  size = 2,
  onHomePage = false,
  moduleRef,
  title
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const { loading, talentPicksActive } = useTalentPicksWithFilters(onHomePage);
  const hasPicksActive = talentPicksActive.length > 0;

  useEffect(() => {
    const moduleOnHome = moduleRef?.current;
    if (moduleOnHome) {
      moduleOnHome.hidden = !hasPicksActive;
    }
  }, [talentPicksActive]);

  if (!hasPicksActive) {
    return null;
  }

  return (
    <TalentPicksContainer theme={theme} data-qa-label={qaLabel}>
      <ContentContainer theme={theme}>
        <DesktopTitleContainer>
          <Paragraph
            qaLabel={`${qaLabel}-title`}
            fontSize={18}
            fontFamily="bold"
          >
            {title || "Talent Picks"}
          </Paragraph>
          <Button
            qaLabel="seeAllPicksLabel"
            onPress={() => navigate("/handicapping/talent-picks")}
            variant="tertiary"
            size="m"
          >
            See all
          </Button>
        </DesktopTitleContainer>
        {loading ? (
          <CardsLoading />
        ) : (
          <>
            {hasPicksActive ? (
              <CardsPgpContainer
                theme={theme}
                hasMultipleCards={false}
                data-qa-label={`${qaLabel}-cards-active`}
              >
                {talentPicksActive
                  .filter((_, index) => !size || index < size)
                  .map((talentPick, i) => (
                    <TalentPicksCard
                      key={talentPick.id}
                      cardPosition={i}
                      talentPick={talentPick}
                      showAddToBetslipButton={
                        talentPick.raceStatus !== RaceStatusEnum.RACE_OFF
                      }
                      isFDTVPage
                    />
                  ))}
                <LinkContainer
                  theme={theme}
                  data-qa-label="seAllPicksLinkContainer"
                >
                  <Link
                    to="/handicapping/talent-picks"
                    data-qa-label="seAllPicksLinkContainerLink"
                    onClick={handleSeeAll}
                  >
                    <AllTalentPicksWrapper>
                      <IconContainer
                        theme={theme}
                        data-qa-label="seAllPicksLinkContainerIconContainer"
                      >
                        <Icon
                          icon={arrowRight}
                          color={theme.colors.blue_accent["600"]}
                          size={24}
                        />
                      </IconContainer>
                      <Paragraph
                        qaLabel="seeAllPicksLabel"
                        fontFamily="bold"
                        fontSize={13}
                        color={theme.colors.blue_accent[600]}
                      >
                        See all Picks
                      </Paragraph>
                    </AllTalentPicksWrapper>
                  </Link>
                </LinkContainer>
              </CardsPgpContainer>
            ) : null}
          </>
        )}
      </ContentContainer>
    </TalentPicksContainer>
  );
};

export { TalentPicksPgP };
export default TalentPickList;
